import { DynamicTextStatus, type TDynamicText } from 'models/common.model'

const EMOJI_JOINER = '\\u200D'
const EMOJI_VARIATION_SELECTOR = '\\uFE0F'
const ALLOWED_DYNAMIC_TEXT_SYMBOLS = `a-zA-Z0-9\\p{Extended_Pictographic} '’‘"“”!?,.:;&%+()-`

const HTML_TAGS = /<[^>]*>/g
// eslint-disable-next-line no-control-regex
const CONTROL_SYMBOLS = /[\u0000-\u001F\u007F]/g
const ZERO_WIDTH_SYMBOLS = /[\u200B\uFEFF]/g
const DISALLOWED_DYNAMIC_TEXT_SYMBOLS = new RegExp(
  `(?!${EMOJI_JOINER})(?!${EMOJI_VARIATION_SELECTOR})[^${ALLOWED_DYNAMIC_TEXT_SYMBOLS}]`,
  'gu',
)
const NON_ENGLISH_LETTER = /(?![a-zA-Z])\p{L}/u
const MEANINGFUL_DYNAMIC_TEXT_SYMBOL = /[a-zA-Z0-9]/
const UNDERSCORES = /_/g
const REPEATED_SPACES = /\s+/g
const TRAILING_PARTIAL_WORD = /\s*\S*$/
const TRAILING_SEPARATORS = /[\s,;:&%+-]+$/

const expandUnderscoresToSpaces = (text: string): string =>
  text.replace(UNDERSCORES, ' ')

const collapseSpaces = (text: string): string =>
  text.replace(REPEATED_SPACES, ' ').trim()

const removeUnsupportedSymbols = (text: string): string =>
  text
    .replace(HTML_TAGS, ' ')
    .replace(ZERO_WIDTH_SYMBOLS, '')
    .replace(CONTROL_SYMBOLS, ' ')
    .replace(DISALLOWED_DYNAMIC_TEXT_SYMBOLS, '')

const cutToLastWholeWord = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text

  const wholeWords = text
    .slice(0, maxLength + 1)
    .replace(TRAILING_PARTIAL_WORD, '')

  const cutText = wholeWords || text.slice(0, maxLength)

  return cutText.replace(TRAILING_SEPARATORS, '')
}

export const getDynamicTextFromSearch = (
  search: string,
  queryParam: string,
  maxLength: number,
): TDynamicText => {
  const textFromUrl = new URLSearchParams(search).get(queryParam)

  if (textFromUrl === null) return { text: '' }

  if (NON_ENGLISH_LETTER.test(textFromUrl)) {
    return { text: '', status: DynamicTextStatus.REJECTED }
  }

  const textWithSpacedUnderscores = expandUnderscoresToSpaces(textFromUrl)
  const normalizedText = collapseSpaces(textWithSpacedUnderscores)
  const sanitizedText = collapseSpaces(
    removeUnsupportedSymbols(textWithSpacedUnderscores),
  )

  if (!MEANINGFUL_DYNAMIC_TEXT_SYMBOL.test(sanitizedText)) {
    return { text: '', status: DynamicTextStatus.REJECTED }
  }

  const text = cutToLastWholeWord(sanitizedText, maxLength)

  return {
    text,
    status:
      text === normalizedText
        ? DynamicTextStatus.APPLIED
        : DynamicTextStatus.VALIDATED,
  }
}
